/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import React from 'react';
import { decorate, runInAction, observable, action, computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Container, Icon } from 'semantic-ui-react';
import { gotoFn } from '@amzn/base-ui/dist/helpers/routing';
import { displayError, displaySuccess } from '@amzn/base-ui/dist/helpers/notification';
import Form from '@amzn/base-ui/dist/parts/helpers/fields/Form';
import Input from '@amzn/base-ui/dist/parts/helpers/fields/Input';
import TextArea from '@amzn/base-ui/dist/parts/helpers/fields/TextArea';
import DropDown from '@amzn/base-ui/dist/parts/helpers/fields/DropDown';
import Toggle from '@amzn/base-ui/dist/parts/helpers/fields/Toggle';

import { getAnnouncementForm } from '../../models/forms/AnnouncementForm';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

class CreateAnnouncement extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.form = getAnnouncementForm();
    });
  }

  get announcementsStore() {
    return this.props.announcementsStore;
  }

  handleCreate = async form => {
    const values = form.values();
    const data = {
      type: values.type,
      content: values.content,
      color: values.color,
      status: values.status === true || values.status === 'true' ? 'active' : 'archived',
      priority: parseInt(values.priority, 10) || 0,
    };
    try {
      await this.announcementsStore.createAnnouncement(data);
      displaySuccess('Announcement created successfully');
      const goto = gotoFn(this);
      goto('/announcements');
    } catch (error) {
      displayError(error);
    }
  };

  handleCancel = () => {
    const goto = gotoFn(this);
    goto('/announcements');
  };

  render() {
    const goto = gotoFn(this);
    const form = this.form;
    return (
      <Container className="mt3 animated fadeIn">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', fontSize: '0.9em', color: '#6b7280' }}>
          <button
            onClick={() => goto('/announcements')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--rmit-blue)', fontWeight: 500, padding: 0, fontFamily: 'inherit', fontSize: 'inherit' }}
          >
            Announcements
          </button>
          <Icon name="angle right" style={iconStyle} />
          <span style={{ color: '#111827', fontWeight: 600 }}>Create Announcement</span>
        </div>

        <div className="workspace-card">
          <div className="workspace-card-accent" style={{ background: 'var(--rmit-blue)' }} />
          <div className="workspace-card-content">
            <Form form={form} onCancel={this.handleCancel} onSuccess={this.handleCreate}>
              {({ processing, onCancel }) => (
                <div>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <div style={{ flex: 1 }}><DropDown field={form.$('type')} /></div>
                    <div style={{ flex: 1 }}><DropDown field={form.$('color')} /></div>
                  </div>
                  <TextArea field={form.$('content')} />
                  <Toggle field={form.$('status')} />
                  <Input field={form.$('priority')} />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
                    <button
                      className="workspace-action-btn workspace-action-btn-secondary"
                      disabled={processing}
                      onClick={onCancel}
                      type="button"
                    >
                      Cancel
                    </button>
                    <button
                      className="workspace-action-btn workspace-action-btn-primary"
                      disabled={processing}
                      type="submit"
                    >
                      <Icon name="newspaper" style={iconStyle} />
                      Create Announcement
                    </button>
                  </div>
                </div>
              )}
            </Form>
          </div>
        </div>
      </Container>
    );
  }
}

decorate(CreateAnnouncement, {
  form: observable,
  announcementsStore: computed,
  handleCreate: action,
  handleCancel: action,
});

export default inject('announcementsStore')(withRouter(observer(CreateAnnouncement)));
